import { createSelector } from 'reselect';
import { getIconUrl, imageEntities } from '../../../utils/ImagesCreator';
import { getCachedSalonCity } from 'utils/salonCityCache';
import { readInviteTotal } from 'Containers/Clients/utils/inviteJourney';

const selectAuthState = (state) => state.authReducer || {};

export const isHomeStatsHidden = (salon) => {
  const hidden = salon?.hideHomeStats ?? salon?.hide_home_stats ?? 0;
  return hidden === 1 || hidden === true;
};

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    // alcuni payload espongono la città come valore non-stringa
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
};

const CITY_KEYS = [
  'citta',
  'cittaSalone',
  'citta_salone',
  'comune',
  'comune_salone',
  'city',
  'localita',
  'nome_citta',
  'nomeCitta',
];
const PROVINCE_KEYS = [
  'provincia',
  'provincia_salone',
  'provinciaSalone',
  'prov',
  'sigla_provincia',
  'siglaProvincia',
];
const ADDRESS_CITY_KEYS = ['citta', 'comune', 'city', 'localita'];
const ADDRESS_PROVINCE_KEYS = ['provincia', 'sigla_provincia'];

const pickKnownKeys = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return '';
  return firstNonEmptyString(...keys.map((key) => obj[key]));
};

/** Città letta dal payload salone (senza side-effect). */
export const readSalonCityFields = (salon) => {
  if (!salon || typeof salon !== 'object') return '';

  const city = firstNonEmptyString(
    pickKnownKeys(salon, CITY_KEYS),
    pickKnownKeys(salon.indirizzo, ADDRESS_CITY_KEYS),
  );
  if (!city) return '';

  const province = firstNonEmptyString(
    pickKnownKeys(salon, PROVINCE_KEYS),
    pickKnownKeys(salon.indirizzo, ADDRESS_PROVINCE_KEYS),
  );

  if (province && !city.toLowerCase().includes(province.toLowerCase())) {
    return `${city} (${province})`;
  }
  return city;
};

export const getSalonCity = (salon) => {
  if (!salon || typeof salon !== 'object') return '';
  return (
    readSalonCityFields(salon) || getCachedSalonCity(salon.idsalone ?? salon.id) || ''
  );
};

/** Selettori stabili: getX() deve restituire la STESSA istanza (memo reselect). */
const selectSalonsRaw = createSelector(selectAuthState, (state) =>
  Array.isArray(state.salons) ? state.salons : [],
);

const selectAuth = createSelector(selectAuthState, (state) => state.isLogin);

const selectActiveStep = createSelector(
  selectAuthState,
  (state) => state.signUpValues?.activeStepId,
);

const selectSalonWorkTimes = createSelector(
  selectAuthState,
  (state) => state.signUpValues?.salonWorkTimes,
);

const selectSalons = createSelector(selectSalonsRaw, (salons) =>
  salons.map((salon) => ({
    name: salon.nome_salone,
    value: salon.idsalone,
    icon: getIconUrl(salon.idsalone, imageEntities.salon, 'profilo'),
    city: getSalonCity(salon),
  })),
);

const selectActiveSalonId = createSelector(
  selectAuthState,
  (state) => state.activeSalonId,
);

const selectActiveSalonHideHomeStats = createSelector(
  selectSalonsRaw,
  selectActiveSalonId,
  (salons, activeSalonId) => {
    const activeSalon = salons.find((salon) => +salon.idsalone === +activeSalonId);
    return isHomeStatsHidden(activeSalon);
  },
);

const selectActiveSalon = createSelector(
  selectSalonsRaw,
  selectActiveSalonId,
  (salons, activeSalonId) => {
    const activeSalon = salons.find((salon) => +salon.idsalone === +activeSalonId);
    return {
      ...activeSalon,
      name: activeSalon?.nome_salone,
      value: activeSalon?.idsalone,
      icon: getIconUrl(activeSalon?.idsalone, imageEntities.salon, 'profilo'),
      coverImg: getIconUrl(activeSalon?.idsalone, imageEntities.salon, 'copertina'),
      city: getSalonCity(activeSalon),
      email: activeSalon?.email,
      numberOfBookableDays: activeSalon?.n_giorni_prenotabili,
      numberOfAppointments: activeSalon?.nMaxPrenotazioni,
      phoneNumber: activeSalon?.tel,
      showPrice: activeSalon?.showPrice,
      pwdPrenotazione: activeSalon?.pwdPrenotazione,
      isOldStyle: activeSalon?.isOldStyle,
      hideHomeStats: activeSalon?.hideHomeStats ?? activeSalon?.hide_home_stats ?? 0,
      demoMode: activeSalon?.demoMode ?? activeSalon?.demo_mode ?? 0,
      passwordStatistiche: Boolean(
        activeSalon?.passwordStatistiche ?? activeSalon?.password_statistiche,
      ),
    };
  },
);

const selectInvitiOnboarding = createSelector(
  selectSalonsRaw,
  selectActiveSalonId,
  (salons, activeSalonId) => {
    const activeSalon = salons.find((salon) => +salon.idsalone === +activeSalonId);
    return activeSalon?.inviti_onboarding ?? 0;
  },
);

const selectInvitiOnboardingTot = createSelector(
  selectSalonsRaw,
  selectActiveSalonId,
  (salons, activeSalonId) => {
    const activeSalon = salons.find((salon) => +salon.idsalone === +activeSalonId);
    return readInviteTotal(activeSalon?.inviti_onboarding_tot) ?? 0;
  },
);

const selectIsSwitchingSalon = createSelector(
  selectAuthState,
  (state) => Boolean(state.isSwitchingSalon),
);

export const getSalonsRaw = () => selectSalonsRaw;
export const getAuth = () => selectAuth;
export const getActiveStep = () => selectActiveStep;
export const salonWorkTimes = () => selectSalonWorkTimes;
export const getSalons = () => selectSalons;
export const getActiveSalonId = () => selectActiveSalonId;
export const getActiveSalonHideHomeStats = () => selectActiveSalonHideHomeStats;
export const getActiveSalon = () => selectActiveSalon;
export const getInvitiOnboarding = () => selectInvitiOnboarding;
export const getInvitiOnboardingTot = () => selectInvitiOnboardingTot;
export const getIsSwitchingSalon = () => selectIsSwitchingSalon;
